/**
 * Precios de la TPV (venta en tienda). El precio CANÓNICO de un artículo es
 * `calculation.subtotal` (ya redondeado hacia arriba a 5 céntimos por la calculadora,
 * IVA incluido): 114,33 -> 114,35. Todo lo demás (detalle, Odoo, PDF) tiene que
 * reconstruir exactamente ese valor a partir de los componentes guardados.
 */

// Redondeo hacia arriba a 5 céntimos (precio final CON IVA)
export const roundToFiveCents = (number) => Math.ceil(number * 20) / 20;

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * Cuadra los componentes de cada artículo para que, al reconstruir el precio en la vista
 * de detalle / Odoo / PDF (suma de componentes x 1,21, redondeado hacia arriba), salga
 * EXACTAMENTE el precio canónico que ya redondeó la calculadora. Sin esto, los componentes
 * se guardan a 2 decimales y su suma "deriva" un céntimo, haciendo que el detalle
 * re-redondee de más (114,35 canónico se mostraba como 114,40). Solo ajusta el céntimo de
 * reparto, sobre el componente mayor.
 *
 * @param {Array} orderItems  líneas a guardar (precio neto por unidad en totalPrice)
 * @param {Array} sourceTicketItems  artículos del ticket (con .calculation y .itemDiscount)
 * @returns {Array} las mismas líneas, mutadas
 */
export const snapOrderItemsToCanonical = (orderItems, sourceTicketItems) => {
  sourceTicketItems.forEach((ti) => {
    const group = orderItems.filter((line) =>
      line.metadata?.fingerprint === ti.calculation?.fingerprint &&
      line.metadata?.calculationIndex === ti.id
    );
    if (group.length === 0) return;
    const units = ti.calculation?.units || 1;
    const disc = ti.itemDiscount || 0;
    const canonicalUnitWithVat = (parseFloat(ti.calculation?.subtotal) / units) * (1 - disc / 100);
    if (!canonicalUnitWithVat || !isFinite(canonicalUnitWithVat)) return;
    // Objetivo (neto por unidad): floor para que roundToFiveCents(neto x 1,21) = precio canónico
    const targetNet = Math.floor((canonicalUnitWithVat / 1.21) * 100) / 100;
    group.forEach((c) => {
      c.totalPrice = r2(c.totalPrice || 0);
      c.unitPrice = c.totalPrice;
    });
    const sumNet = r2(group.reduce((s, c) => s + (c.totalPrice || 0), 0));
    const diff = r2(targetNet - sumNet);
    if (diff !== 0) {
      const largest = group.reduce((a, b) => ((b.totalPrice || 0) > (a.totalPrice || 0) ? b : a), group[0]);
      largest.totalPrice = r2(largest.totalPrice + diff);
      largest.unitPrice = largest.totalPrice;
    }
  });
  return orderItems;
};
